// ============================================================
// WorkbenchContext — the persistent Agent Workbench session, mounted ONCE in
// the shell so an active agent task stays reachable as the owner navigates.
//
// Presentation state (open/minimized, the compose draft) lives here; TASK truth
// lives in the backend (sq_agent_tasks + events) and is fetched by the panel.
// Nothing protected is persisted to localStorage — only the presentation
// preference of the active task id, so a refresh can reopen it from server truth.
// ============================================================

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { CommandContext } from './useCommandContext'

type WorkbenchMode = 'closed' | 'compose' | 'task'
const ACTIVE_TASK_KEY = 'sc.workbench.activeTask'

export interface WorkbenchState {
  mode: WorkbenchMode
  minimized: boolean
  composeInstruction: string
  composeContext: CommandContext | null
  activeTaskId: string | null
  /** Set by the panel from real task state so the command bar can badge attention. */
  attention: 'none' | 'approval' | 'running'
  startAgentFlow: (instruction: string, context: CommandContext) => void
  openTask: (taskId: string) => void
  setAttention: (a: WorkbenchState['attention']) => void
  minimize: () => void
  restore: () => void
  close: () => void
}

const Ctx = createContext<WorkbenchState | null>(null)

function readStoredTask(): string | null {
  try { return window.localStorage.getItem(ACTIVE_TASK_KEY) } catch { return null }
}
function storeTask(id: string | null) {
  try { if (id) window.localStorage.setItem(ACTIVE_TASK_KEY, id); else window.localStorage.removeItem(ACTIVE_TASK_KEY) } catch { /* ignore */ }
}

export function WorkbenchProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<WorkbenchMode>('closed')
  const [minimized, setMinimized] = useState(false)
  const [composeInstruction, setComposeInstruction] = useState('')
  const [composeContext, setComposeContext] = useState<CommandContext | null>(null)
  const [activeTaskId, setActiveTaskId] = useState<string | null>(() => readStoredTask())
  const [attention, setAttention] = useState<WorkbenchState['attention']>('none')

  const startAgentFlow = useCallback((instruction: string, context: CommandContext) => {
    setComposeInstruction(instruction)
    setComposeContext(context)
    setMode('compose')
    setMinimized(false)
  }, [])

  const openTask = useCallback((taskId: string) => {
    setActiveTaskId(taskId)
    storeTask(taskId)
    setMode('task')
    setMinimized(false)
  }, [])

  const minimize = useCallback(() => setMinimized(true), [])
  const restore = useCallback(() => { setMinimized(false); if (mode === 'closed' && activeTaskId) setMode('task') }, [mode, activeTaskId])
  const close = useCallback(() => { setMode('closed'); setMinimized(false); setAttention('none') }, [])

  const value = useMemo<WorkbenchState>(() => ({
    mode, minimized, composeInstruction, composeContext, activeTaskId, attention,
    startAgentFlow, openTask, setAttention, minimize, restore, close,
  }), [mode, minimized, composeInstruction, composeContext, activeTaskId, attention, startAgentFlow, openTask, minimize, restore, close])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useWorkbench(): WorkbenchState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useWorkbench must be used within WorkbenchProvider')
  return v
}
